import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const testimonials = [
  {
    name: 'Ing. Carlos Medina',
    role: 'Supervisor de Perforación',
    company: 'Operadora Petrolera del Oriente',
    region: 'Monagas, Venezuela',
    avatar: 'CM',
    color: 'bg-primary-500',
    stars: 5,
    text: 'Llevábamos meses con problemas de viscosidad en nuestros fluidos de perforación. Desde que aplicamos los productos de Poly Alphas, los indicadores se normalizaron en la primera semana. El soporte técnico fue inmediato y los ingenieros vinieron al campo a resolver el problema personalmente. No es solo un proveedor, es un aliado real.',
    metric: 'Viscosidad estabilizada en 7 días',
    metricIcon: 'ri-bar-chart-line',
  },
  {
    name: 'Ing. Rosa Blanco',
    role: 'Jefa de Producción',
    company: 'Empresa Mixta Petroanzoátegui',
    region: 'Anzoátegui, Venezuela',
    avatar: 'RB',
    color: 'bg-accent-500',
    stars: 5,
    text: 'El ALPHA-29 superó todas nuestras expectativas. Teníamos una emulsión de crudo-agua que nos generaba pérdidas millonarias mensualmente. Después de aplicar el demulsificante, la separación fue visible en minutos y la producción aumentó considerablemente. El producto cumplió exactamente lo que prometieron y el precio fue muy competitivo versus opciones importadas.',
    metric: '+18% de recuperación de crudo',
    metricIcon: 'ri-drop-line',
  },
  {
    name: 'Lic. Manuel Torres',
    role: 'Gerente de Compras',
    company: 'Servicios Técnicos Lacustres C.A.',
    region: 'Zulia, Venezuela',
    avatar: 'MT',
    color: 'bg-primary-600',
    stars: 5,
    text: 'Lo que más valoro de Poly Alphas es la seriedad. Cada pedido llega con su ficha técnica, MSDS y certificado de análisis. El stock siempre está disponible y el despacho es rápido. Hemos probado otros proveedores pero con Poly Alphas la calidad es constante, lote tras lote. Eso para nosotros es invaluable en operaciones críticas.',
    metric: 'Proveedor oficial desde 2019',
    metricIcon: 'ri-shield-check-line',
  },
  {
    name: 'Ing. Andreína Rojas',
    role: 'Directora Técnica',
    company: 'Perforaciones del Sur C.A.',
    region: 'Apure, Venezuela',
    avatar: 'AR',
    color: 'bg-accent-600',
    stars: 5,
    text: 'Compramos las Resinas Demulsificantes y la diferencia con las que usábamos antes fue inmediata. Mayor pureza, mejor desempeño y con certificados que cumplen todos nuestros estándares corporativos. Además, el equipo técnico de Poly Alphas nos ayudó a ajustar las dosificaciones para nuestra operación específica. Eso no lo encuentras en cualquier proveedor.',
    metric: 'Reducción de 40% en consumo de resina',
    metricIcon: 'ri-recycle-line',
  },
];

const cases = [
  {
    title: 'Tratamiento de Emulsión — Pozo Complejo',
    company: 'Empresa Mixta en Monagas',
    challenge: 'Emulsión de crudo-agua estable con alta presencia de asfaltenos que bloqueaba la producción en 3 pozos simultáneamente.',
    solution: 'Aplicación de ALPHA-29 con protocolo de dosis escalonada diseñado por nuestro equipo técnico para las condiciones específicas del yacimiento.',
    result: 'Separación completa en menos de 4 horas. Producción recuperada al 100% en los 3 pozos dentro de 48 horas.',
    tags: ['ALPHA-29', 'Demulsificante', 'Producción'],
    icon: 'ri-drop-line',
    iconBg: 'bg-primary-500/15',
    iconColor: 'text-primary-500',
  },
  {
    title: 'Formulación de Fluido de Perforación',
    company: 'Contratista en Anzoátegui',
    challenge: 'Fluido con pérdida de filtrado excesiva y comportamiento reológico inestable en formaciones de alta temperatura.',
    solution: 'Reformulación completa usando nuestra línea de productos para perforación con seguimiento técnico semanal en campo.',
    result: 'Pérdida de filtrado reducida en un 60%. Comportamiento reológico estable a 180°F. Sin eventos no productivos en el pozo.',
    tags: ['Fluidos de Perforación', 'Perforación', 'HPHT'],
    icon: 'ri-test-tube-line',
    iconBg: 'bg-accent-500/15',
    iconColor: 'text-accent-500',
  },
  {
    title: 'Suministro de Resinas Demulsificantes',
    company: 'Planta de Tratamiento en Zulia',
    challenge: 'Desabastecimiento de resinas demulsificantes importadas con tiempos de espera superiores a 3 meses.',
    solution: 'Suministro inmediato desde nuestro inventario local con documentación técnica completa y protocolo de incorporación asistido.',
    result: 'Planta operativa en 72 horas. Ahorro del 25% en costo vs. importación directa. Contrato de suministro anual firmado.',
    tags: ['Resinas Demulsificantes', 'Stock Inmediato', 'Zulia'],
    icon: 'ri-stack-line',
    iconBg: 'bg-primary-500/15',
    iconColor: 'text-primary-500',
  },
];

export default function Testimonials() {
  const [visible, setVisible] = useState(false);
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [activeTab, setActiveTab] = useState<'testimonials' | 'cases'>('testimonials');
  const navigate = useNavigate();
  const sectionRef = useRef<HTMLDivElement>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const next = useCallback(() => {
    setActiveTestimonial((c) => (c + 1) % testimonials.length);
  }, []);

  useEffect(() => {
    if (activeTab !== 'testimonials') return;
    intervalRef.current = setInterval(next, 5000);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [next, activeTab]);

  const handleTabChange = (tab: 'testimonials' | 'cases') => {
    setActiveTab(tab);
    if (intervalRef.current) clearInterval(intervalRef.current);
  };

  const t = testimonials[activeTestimonial];

  return (
    <section ref={sectionRef} className="py-20 md:py-28 bg-background-950 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-primary-500/5 -translate-y-1/2 translate-x-1/2"></div>
      <div className="absolute bottom-0 left-0 w-80 h-80 rounded-full bg-accent-500/5 translate-y-1/2 -translate-x-1/2"></div>

      <div className="max-w-7xl mx-auto px-4 md:px-6 relative z-10">

        {/* Header */}
        <div className={`text-center mb-12 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="text-accent-400 text-sm font-600 tracking-widest uppercase mb-3 block">
            Resultados Reales
          </span>
          <h2 className="font-heading font-700 text-4xl md:text-5xl text-white leading-tight mb-4">
            Lo Que Dicen Nuestros <span className="gradient-text">Clientes</span>
          </h2>
          <div className="section-divider mx-auto mb-6"></div>
          <p className="text-foreground-400 text-base max-w-2xl mx-auto">
            Más de 200 empresas del sector petrolero venezolano confían en nuestros productos.
            Estos son algunos de sus resultados.
          </p>
        </div>

        {/* Tab switcher */}
        <div className={`flex justify-center mb-10 transition-all duration-700 delay-100 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <div className="inline-flex bg-background-800 border border-background-700 rounded-xl p-1 gap-1">
            <button
              onClick={() => handleTabChange('testimonials')}
              className={`px-6 py-2.5 rounded-lg text-sm font-600 transition-all duration-200 cursor-pointer whitespace-nowrap flex items-center gap-2 ${
                activeTab === 'testimonials'
                  ? 'bg-primary-500 text-white'
                  : 'text-foreground-400 hover:text-white hover:bg-background-700'
              }`}
            >
              <i className="ri-chat-quote-line"></i>
              Testimonios
            </button>
            <button
              onClick={() => handleTabChange('cases')}
              className={`px-6 py-2.5 rounded-lg text-sm font-600 transition-all duration-200 cursor-pointer whitespace-nowrap flex items-center gap-2 ${
                activeTab === 'cases'
                  ? 'bg-primary-500 text-white'
                  : 'text-foreground-400 hover:text-white hover:bg-background-700'
              }`}
            >
              <i className="ri-file-chart-line"></i>
              Casos de Éxito
            </button>
          </div>
        </div>

        {/* TESTIMONIALS VIEW */}
        {activeTab === 'testimonials' && (
          <div className={`transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="grid lg:grid-cols-3 gap-8 items-start">

              {/* Main testimonial card */}
              <div className="lg:col-span-2 bg-background-900 border border-background-700 rounded-2xl p-8 md:p-10">
                {/* Quote icon */}
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-500/15 mb-6">
                  <i className="ri-double-quotes-l text-primary-400 text-2xl"></i>
                </div>

                {/* Stars */}
                <div className="flex gap-1 mb-4">
                  {Array.from({ length: t.stars }).map((_, i) => (
                    <i key={i} className="ri-star-fill text-amber-400 text-lg"></i>
                  ))}
                </div>

                {/* Text */}
                <p className="text-foreground-200 text-base md:text-lg leading-relaxed mb-8 italic">
                  &ldquo;{t.text}&rdquo;
                </p>

                {/* Metric highlight */}
                <div className="flex items-center gap-3 mb-8 p-4 rounded-xl bg-primary-500/10 border border-primary-500/20">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-500/20 flex-shrink-0">
                    <i className={`${t.metricIcon} text-primary-400 text-lg`}></i>
                  </div>
                  <div>
                    <p className="text-white text-sm font-700">{t.metric}</p>
                    <p className="text-foreground-500 text-xs">Resultado verificado en campo</p>
                  </div>
                </div>

                {/* Author */}
                <div className="flex items-center gap-4 pt-6 border-t border-background-700">
                  <div className={`w-14 h-14 flex items-center justify-center rounded-full ${t.color} text-white font-700 text-lg flex-shrink-0`}>
                    {t.avatar}
                  </div>
                  <div>
                    <p className="text-white font-700 text-base">{t.name}</p>
                    <p className="text-foreground-400 text-sm">{t.role} · {t.region}</p>
                  </div>
                </div>
              </div>

              {/* Side: list of testimonials */}
              <div className="flex flex-col gap-3">
                {testimonials.map((item, i) => (
                  <button
                    key={i}
                    onClick={() => {
                      setActiveTestimonial(i);
                      if (intervalRef.current) clearInterval(intervalRef.current);
                    }}
                    className={`text-left p-4 rounded-xl border transition-all duration-200 cursor-pointer ${
                      i === activeTestimonial
                        ? 'bg-primary-500/10 border-primary-500/40'
                        : 'bg-background-800 border-background-700 hover:border-background-500'
                    }`}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <div className={`w-9 h-9 flex items-center justify-center rounded-full ${item.color} text-white text-xs font-700 flex-shrink-0`}>
                        {item.avatar}
                      </div>
                      <div>
                        <p className={`text-sm font-700 leading-tight ${i === activeTestimonial ? 'text-white' : 'text-foreground-300'}`}>
                          {item.name}
                        </p>
                        <p className="text-foreground-500 text-xs">{item.role} · {item.region}</p>
                      </div>
                    </div>
                    <div className="flex gap-0.5 mb-2">
                      {Array.from({ length: item.stars }).map((_, si) => (
                        <i key={si} className="ri-star-fill text-amber-400 text-xs"></i>
                      ))}
                    </div>
                    <p className={`text-xs leading-relaxed line-clamp-2 ${i === activeTestimonial ? 'text-foreground-300' : 'text-foreground-500'}`}>
                      {item.text}
                    </p>
                  </button>
                ))}

                {/* Auto-progress dots */}
                <div className="flex justify-center gap-2 pt-2">
                  {testimonials.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setActiveTestimonial(i)}
                      className={`rounded-full transition-all duration-300 cursor-pointer ${
                        i === activeTestimonial ? 'w-6 h-2 bg-primary-500' : 'w-2 h-2 bg-background-600 hover:bg-background-400'
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* CASES VIEW */}
        {activeTab === 'cases' && (
          <div className={`transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="grid md:grid-cols-3 gap-6">
              {cases.map((c, i) => (
                <div
                  key={c.title}
                  className="bg-background-900 border border-background-700 rounded-2xl overflow-hidden flex flex-col transition-all duration-500 hover:border-primary-500/40"
                  style={{ transitionDelay: `${i * 0.1}s` }}
                >
                  {/* Card header */}
                  <div className="p-6 border-b border-background-800">
                    <div className={`w-12 h-12 flex items-center justify-center rounded-xl ${c.iconBg} mb-4`}>
                      <i className={`${c.icon} ${c.iconColor} text-2xl`}></i>
                    </div>
                    <h3 className="font-heading font-700 text-lg text-white mb-1 leading-tight">{c.title}</h3>
                    <p className="text-foreground-500 text-xs flex items-center gap-1.5">
                      <i className="ri-building-2-line"></i>
                      {c.company}
                    </p>
                  </div>

                  {/* Card body */}
                  <div className="p-6 flex flex-col gap-5 flex-1">
                    <div>
                      <p className="text-accent-400 text-xs font-700 tracking-widest uppercase mb-2 flex items-center gap-1.5">
                        <i className="ri-error-warning-line"></i> Desafío
                      </p>
                      <p className="text-foreground-400 text-sm leading-relaxed">{c.challenge}</p>
                    </div>
                    <div>
                      <p className="text-primary-400 text-xs font-700 tracking-widest uppercase mb-2 flex items-center gap-1.5">
                        <i className="ri-lightbulb-line"></i> Solución Aplicada
                      </p>
                      <p className="text-foreground-400 text-sm leading-relaxed">{c.solution}</p>
                    </div>
                    <div className="p-4 rounded-xl bg-primary-500/8 border border-primary-500/20">
                      <p className="text-primary-400 text-xs font-700 tracking-widest uppercase mb-2 flex items-center gap-1.5">
                        <i className="ri-trophy-line"></i> Resultado
                      </p>
                      <p className="text-foreground-200 text-sm leading-relaxed font-500">{c.result}</p>
                    </div>

                    {/* Tags */}
                    <div className="flex flex-wrap gap-2 mt-auto">
                      {c.tags.map((tag) => (
                        <span key={tag} className="px-2.5 py-1 rounded-full text-xs bg-background-800 text-foreground-400 border border-background-700">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Bottom CTA */}
        <div className={`mt-14 text-center transition-all duration-700 delay-400 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <p className="text-foreground-400 text-sm mb-4">
            ¿Listo para ser el próximo caso de éxito?
          </p>
          <button
            onClick={() => navigate('/contacto')}
            className="inline-flex items-center gap-2 px-8 py-4 bg-primary-500 hover:bg-primary-400 text-white font-700 rounded-xl transition-all duration-200 cursor-pointer whitespace-nowrap text-base"
          >
            <i className="ri-mail-send-line"></i>
            Hablar con un Especialista
          </button>
        </div>

      </div>
    </section>
  );
}